import { Horizontal, Vertical } from '@gravitate-js/excalibrr'
import { useVirtualizer } from '@tanstack/react-virtual'
import { Skeleton } from 'antd'
import React from 'react'

type RowLoadingSkeletonProps = {
  rowCount: number
}

type VirtualListProps<T> = {
  data: T[]
  itemRenderer: (data: T, index: number) => React.ReactNode
  isLoading: boolean
}

function RowLoadingSkeleton({ rowCount }: RowLoadingSkeletonProps) {
  return (
    <Vertical>
      {Array.from({ length: rowCount }).map((_, index) => (
        <Skeleton key={index} active />
      ))}
    </Vertical>
  )
}

export function VirtualList<T>({ data, itemRenderer, isLoading }: VirtualListProps<T>) {
  const parentRef = React.useRef(null)
  const virtualizer = useVirtualizer({
    count: data?.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 100,
    overscan: 5,
  })

  const items = virtualizer.getVirtualItems()

  return (
    <div
      ref={parentRef}
      id='parent'
      style={{ height: '100%', overflow: 'auto', contain: 'strict', scrollbarWidth: 'none' }}
    >
      {isLoading && <RowLoadingSkeleton rowCount={10} />}
      {!isLoading && !data?.length && (
        <Horizontal verticalCenter horizontalCenter height={'100%'}>
          No results
        </Horizontal>
      )}
      <div
        style={{
          height: virtualizer.getTotalSize(),
          position: 'relative',
          marginBottom: '30vh' /* ensures list is scrollable on mobile */,
        }}
      >
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            transform: `translateY(${items[0]?.start ?? 0}px)`,
          }}
        >
          {virtualizer.getVirtualItems().map((item) => (
            <div key={item.key} data-index={item.index} ref={virtualizer.measureElement}>
              {itemRenderer(data[item.index], item.index)}
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
